import { patchState, signalStore, withHooks, withMethods, withState } from '@ngrx/signals'
import { isEqual } from 'lodash'

import { Product } from 'app/_shared/interfaces/Product'
import { priceForQuantity } from 'app/_shared/services/pricing.utils'

import { withLocalStorageSync } from './with-local-storage-sync'

// Floors a requested quantity to the item's minimum order quantity, when it has one.
function resolveMinQuantity(
  item: Pick<Product, 'minimum_order_quantity' | 'minimum_order_quantity_amount'>,
  quantity: number
): number {
  if (item.minimum_order_quantity && item.minimum_order_quantity_amount) {
    return Math.max(quantity, item.minimum_order_quantity_amount)
  }
  return quantity
}

type CartState = {
  items: Product[]
  isLoading: boolean
  hideProductPrices: boolean
  giftingToken: string
  giftingUserToken: string
}

const initialState: CartState = {
  items: [],
  isLoading: false,
  hideProductPrices: false,
  giftingToken: '',
  giftingUserToken: ''
}

export const CartStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withLocalStorageSync('cart'),
  withMethods(store => ({
    addCartItem(item: any): void {
      patchState(store, state => {
        // Find the index of the existing item with the same id, product_id, size and color and custom inputs
        const existingItemIndex = state.items.findIndex(cartItem => {
          return (
            cartItem.id === item.id &&
            cartItem.product_id === item.product_id &&
            cartItem.size === item.size &&
            cartItem.color === item.color &&
            cartItem.email === item.email &&
            isEqual(cartItem.input_fields, item.input_fields)
          )
        })

        if (existingItemIndex !== -1) {
          // Item exists, update its quantity
          const updatedItems = state.items.map((cartItem, index) => {
            if (index === existingItemIndex) {
              const newQuantity = resolveMinQuantity(cartItem, cartItem.quantity + item.quantity)
              const newPrice = priceForQuantity(cartItem, newQuantity)
              return {
                ...cartItem,
                quantity: newQuantity,
                price: newPrice,
                final_price: newPrice
              }
            }
            return cartItem
          })
          return { items: updatedItems }
        } else {
          return {
            items: [...state.items, { ...item, quantity: resolveMinQuantity(item, item.quantity) }]
          }
        }
      })
      store.saveToLocalStorage()
    },
    removeCartItem(index: number): void {
      patchState(store, state => {
        const itemArray = state.items.filter((item, i) => index !== i)
        return { items: [...itemArray] }
      })
      store.saveToLocalStorage()
    },
    updateQuantity(index: number, quantity: number): void {
      patchState(store, state => {
        const itemArray = state.items.map((item, i) => {
          if (index === i) {
            const newQuantity = resolveMinQuantity(item, quantity)
            const newPrice = priceForQuantity(item, newQuantity)
            return { ...item, quantity: newQuantity, price: newPrice, final_price: newPrice }
          }
          return item
        })
        return { items: [...itemArray] }
      })
      store.saveToLocalStorage()
    },
    clearCart(): void {
      patchState(store, () => ({
        items: [],
        giftingToken: '',
        giftingUserToken: '',
        hideProductPrices: false
      }))
      store.removeFromLocalStorage()
    },
    setGiftToken(token: string, userToken: string, hidePrices): void {
      patchState(store, () => ({
        giftingToken: token,
        giftingUserToken: userToken,
        hideProductPrices: hidePrices
      }))
      store.saveToLocalStorage()
    },
    removeGiftToken(): void {
      patchState(store, () => ({
        giftingToken: '',
        giftingUserToken: '',
        hideProductPrices: false
      }))
      store.saveToLocalStorage()
    }
  })),
  withHooks(store => ({
    onInit() {
      if (store.loadFromLocalStorage()) {
        return
      }
    }
  }))
)
